import type { FormEvent } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  createProperty,
  type PropertyPayload,
} from "../../imoveis/imoveisApi";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para criar imóveis.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível criar o imóvel.";
};

export function NovoImovelView() {
  const navigate = useNavigate();
  const [nomeImovel, setNomeImovel] = useState("");
  const [morada, setMorada] = useState("");
  const [tipologia, setTipologia] = useState("");
  const [area, setArea] = useState("");
  const [renda, setRenda] = useState("");
  const [imi, setImi] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!nomeImovel.trim()) {
      setSubmitError("O nome/identificador do imóvel é obrigatório.");
      return;
    }

    setIsSaving(true);

    const payload: PropertyPayload = {
      name: nomeImovel.trim(),
      address: morada.trim() || null,
      propertyType: tipologia.trim() || null,
      area: area !== "" ? parseFloat(area) : null,
      rentValue: renda !== "" ? parseFloat(renda) : null,
      annualImi: imi !== "" ? parseFloat(imi) : null,
    };

    try {
      await createProperty(payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    navigate("/imoveis", {
      state: { toast: "Imóvel criado com sucesso" },
    });
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/imoveis")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "novo-imovel-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Imóvel"}
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Novo Imóvel"
        subtitle="Regista um novo imóvel e define os seus dados."
        actions={actions}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <form
            id="novo-imovel-form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados do Imóvel
                  </h3>

                  <div>
                    <label
                      htmlFor="nomeImovel"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Identificador do Imóvel{" "}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      id="nomeImovel"
                      type="text"
                      placeholder="Insira o nome/identificador do imóvel"
                      value={nomeImovel}
                      onChange={(event) => setNomeImovel(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="morada"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Morada
                    </label>
                    <input
                      id="morada"
                      type="text"
                      placeholder="Rua, número, código postal, localidade"
                      value={morada}
                      onChange={(event) => setMorada(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="tipologia"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Tipologia
                    </label>
                    <input
                      id="tipologia"
                      type="text"
                      placeholder="Ex: T1, T2, Loja, Armazém"
                      value={tipologia}
                      onChange={(event) => setTipologia(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="area"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Área (m²)
                    </label>
                    <input
                      id="area"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={area}
                      onChange={(event) => setArea(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados Financeiros
                  </h3>

                  <div>
                    <label
                      htmlFor="renda"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Valor de Renda (€)
                    </label>
                    <input
                      id="renda"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={renda}
                      onChange={(event) => setRenda(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="imi"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      IMI Anual (€)
                    </label>
                    <input
                      id="imi"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={imi}
                      onChange={(event) => setImi(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
